import { Property } from '@/components/form/fields/select/SelectInput';
import { DEVICE_FEE_CATEGORY_ID } from '@/pages/product/consts';
import { RoleType } from '@/services/authentication/hooks/user';
import {
  AdminCatalogueConfiguration,
  CompanyCatalogueConfiguration,
  PartnerCatalogueConfiguration
} from '@/services/catalogue/catalogue';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';

export const useManufacturers = () => {
  return useAPIQuery(['getManufacturers'], async client => {
    const response = await client.catalog.manufacturers.getManufacturers();
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.items;
  });
};

export const useCategories = () => {
  // ideally this call would be used rather than manually getting the preferences below.
  // However, the cache for this seems to only be cleared after the below is executed.
  //const [preferences] = useDisplayPreferences();

  return useAPIQuery(['getCategories'], async client => {
    const response = await client.catalog.categories.getCategories();
    if (response.status !== 200) throw APIError.fromResponse(response);
    if (response.body.items) {
      response.body.items.push(
        ...response.body.items.splice(
          response.body.items.findIndex(item => item.id === DEVICE_FEE_CATEGORY_ID),
          1
        )
      );
    }
    const preferencesResponse = await client.settings.getDisplayPreferences();
    if (preferencesResponse.status !== 200) throw APIError.fromResponse(preferencesResponse);

    return response.body.items.sort((a, b) => {
      const indexA = preferencesResponse.body.category.displayOrderIds.indexOf(a.id);
      const indexB = preferencesResponse.body.category.displayOrderIds.indexOf(b.id);
      return indexA - indexB;
    });
  });
};

export const useCategory = (id: string) => {
  return useAPIQuery(['getCategory', id], async client => {
    const response = await client.catalog.categories.getCategory({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useMainDeviceCategories = () => {
  const [categories] = useCategories();

  return categories.filter(category => category.isMainDevice);
};

export const useCatalogue = (
  configuration: CompanyCatalogueConfiguration | PartnerCatalogueConfiguration | AdminCatalogueConfiguration
) => {
  return useAPIQuery(['getCatalogue', configuration], async client => {
    if (configuration.for === RoleType.Admin) {
      const response = await client.catalog.catalog.getDaaSCatalog();
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    }

    if (configuration.for === RoleType.Partner) {
      const response = await client.catalog.catalog.getMSPCatalog({
        params: { partnerId: configuration.partnerId }
      });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    }

    if (configuration.for === RoleType.Company) {
      if (configuration.as === RoleType.Company) return null;
      const response = await client.catalog.catalog.getCustomerCatalog({
        params: { companyId: configuration.companyId, partnerId: configuration.partnerId }
      });
      if (response.status !== 200) throw APIError.fromResponse(response);
      return response.body;
    }
  });
};

export const useMspBaseCatalogue = (id: string) => {
  return useAPIQuery(['getMspBaseCatalogue', id], async client => {
    const response = await client.catalog.catalog.getBaseCatalogForMsp({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useFilterPack = (filterPackId: string) => {
  return useAPIQuery(['getFilterPack', filterPackId], async client => {
    const response = await client.catalog.filters.getFilterPack({ params: { filterPackId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

export const useCatalogueBundles = (partnerId: string, companyId: string) => {
  return useAPIQuery(['getCatalogueBundles', companyId], async client => {
    const response = await client.catalog.catalog.getCatalogueBundles({ params: { companyId, partnerId } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.items;
  });
};

export enum FilterType {
  Hidden = 'hidden',
  Margin = 'margin'
}

interface CreateFilterArgs {
  type: FilterType;
  criteria: Record<string, Property>;
}

interface CreateMarginFilterArgs extends CreateFilterArgs {
  type: FilterType.Margin;
  value?: number;
}

interface CreateProductFilterArgs extends CreateFilterArgs {
  type: FilterType.Hidden;
}

interface CreateProductMarginFilterArgs {
  value: number;
  id: string;
}

export const useCreateProductMarginFilter = () => {
  return useAPIMutation(client => async (args: CreateProductMarginFilterArgs) => {
    if (typeof args.value !== 'number') {
      throw new Error('Margin filter requires a value');
    }
    const daasCatalogItemId = args.id;

    const response = await client.catalog.filters.createMarginFilter({
      body: {
        effectiveDate: {
          value: new Date()
        },
        modifier: args.value,
        daasCatalogItemId
      }
    });
    if (response.status !== 200) {
      throw APIError.fromResponse(response);
    }
    return response.body;
  });
};

export const useCreateFilter = () => {
  return useAPIMutation((client, query) => async (args: CreateMarginFilterArgs | CreateProductFilterArgs) => {
    const categoryId = args.criteria?.category?.id;
    const manufacturerId = args.criteria?.manufacturer?.id;

    if (args.type === FilterType.Margin) {
      if (!args.value) {
        throw new Error('Margin filter requires a value');
      }

      const response = await client.catalog.filters.createMarginFilter({
        body: {
          effectiveDate: {
            value: new Date()
          },
          modifier: args.value,
          categoryId,
          manufacturerId
        }
      });
      if (response.status !== 200) {
        throw APIError.fromResponse(response);
      }

      await query.invalidateQueries(['getFilterPack']);
      await query.invalidateQueries(['getProducts']);
      return response.body;
    }

    if (args.type === FilterType.Hidden) {
      const response = await client.catalog.filters.createProductFilter({
        body: {
          effectiveDate: {
            value: new Date()
          },
          categoryId,
          manufacturerId
        }
      });
      if (response.status !== 200) {
        throw APIError.fromResponse(response);
      }

      await query.invalidateQueries(['getFilterPack']);
      await query.invalidateQueries(['getProducts']);
      return response.body;
    }

    throw new Error('Invalid filter type');
  });
};

interface CreateIndividualProductFilterArgs {
  productId: string;
  effectiveDate: Date;
}

export const useCreateProductFilter = () => {
  return useAPIMutation(client => async ({ productId, effectiveDate }: CreateIndividualProductFilterArgs) => {
    const response = await client.catalog.filters.createProductFilter({
      body: {
        effectiveDate: {
          value: effectiveDate
        },
        daasCatalogItemId: productId
      }
    });
    if (response.status !== 200) {
      throw APIError.fromResponse(response);
    }
    return response.body;
  });
};

export const useProductFilter = (id: string) => {
  return useAPIQuery(['getProductFilter', id], async client => {
    const response = await client.catalog.filters.getProductFilter({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface DeleteProductFilterArgs {
  id: string;
}

export const useDeleteProductFilter = () => {
  return useAPIMutation((client, query) => {
    return async ({ id }: DeleteProductFilterArgs) => {
      const response = await client.catalog.filters.deleteProductFilter({ params: { id } });
      if (response.status !== 200) {
        throw APIError.fromResponse(response);
      }
      await query.invalidateQueries(['getFilterPack']);
      await query.invalidateQueries(['getProducts']);
      return response;
    };
  });
};

export interface UpdateFilterPackArgs {
  id: string;
  globalMarginModifier: number;
  marginFilterIds: string[];
  productFilterIds: string[];
}

function delay(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export const useUpdateFilterPack = () => {
  const configuration = useCatalogueService(state => state.configuration);
  return useAPIMutation((client, query) => async (args: UpdateFilterPackArgs) => {
    const response = await client.catalog.filters.updateFilterPack({
      params: { id: args.id },
      body: args
    });
    if (response.status !== 200) {
      throw APIError.fromResponse(response);
    }
    await delay(1000);
    await query.invalidateQueries(['getFilterPack', response.body.id]);
    await query.invalidateQueries(['getProducts']);
    if ('partnerId' in configuration) {
      const c = configuration as PartnerCatalogueConfiguration;
      await query.invalidateQueries(['getMspBaseCatalogue', c.partnerId]);
    }
    await query.invalidateQueries(['getCatalogue']);
    return response.body;
  });
};

export const useMarginFilter = (id: string) => {
  return useAPIQuery(['getMarginFilter', id], async client => {
    const response = await client.catalog.filters.getMarginFilter({ params: { id } });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body;
  });
};

interface DeleteMarginFilterArgs {
  id: string;
}

export const useDeleteMarginFilter = () => {
  return useAPIMutation((client, query) => {
    return async ({ id }: DeleteMarginFilterArgs) => {
      const response = await client.catalog.filters.deleteMarginFilter({ params: { id } });
      if (response.status !== 200) {
        throw APIError.fromResponse(response);
      }
      await query.invalidateQueries(['getFilterPack']);
      await query.invalidateQueries(['getProducts']);
      return response;
    };
  });
};

export interface CreateBundleArgs {
  companyId: string;
  partnerId: string;
  name: string;
  description: string;
  bundledItems: {
    catalogItem: string;
    count: number;
  }[];
  unbundledItems: {
    catalogItem: string;
    count: number;
  }[];
}

interface CreateManufacturerArgs {
  name: string;
}

export const useCreateManufacturer = () => {
  return useAPIMutation((client, query) => async ({ name }: CreateManufacturerArgs) => {
    const response = await client.catalog.manufacturers.createManufacturer({ body: { name } });
    if (response.status !== 200) {
      throw APIError.fromResponse(response);
    }
    await query.invalidateQueries(['getManufacturers']);
    return response.body;
  });
};

interface CreateCategoryArgs {
  name: string;
  description: string;
  isMainDevice: boolean;
}

export const useCreateCategory = () => {
  return useAPIMutation((client, query) => async ({ name, description, isMainDevice }: CreateCategoryArgs) => {
    const response = await client.catalog.categories.createCategory({ body: { name, description, isMainDevice } });
    if (response.status !== 200) {
      throw APIError.fromResponse(response);
    }
    await query.invalidateQueries(['getCategories']);
    await query.invalidateQueries(['getDisplayPreferences']);
    return response.body;
  });
};

export const useUpdateCategory = () => {
  return useAPIMutation(
    (client, query) =>
      async ({ id, name, description, isMainDevice }: CreateCategoryArgs & { id: string }) => {
        const response = await client.catalog.categories.updateCategory({
          params: { categoryId: id },
          body: { name, description, isMainDevice }
        });
        if (response.status !== 200) {
          throw APIError.fromResponse(response);
        }
        await query.invalidateQueries(['getCategories']);
        // Need to invalidate due to potential main device categories being changed, thus needing to be reflected in the bundled products
        await query.invalidateQueries(['getBundles']);
        return response.body;
      }
  );
};
